const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim()
const INTERNAL_STORAGE_KEY = 'wyrdgrove_ga_internal'

let initialized = false
let initScheduled = false

export function isAnalyticsEnabled() {
  return Boolean(MEASUREMENT_ID) && !isAnalyticsSkipped()
}

/** Questo browser non invia eventi GA (visita una volta `?ga_internal=1`). */
export function isAnalyticsSkipped() {
  if (typeof window === 'undefined') return false
  if (import.meta.env.DEV) return true
  try {
    return window.localStorage.getItem(INTERNAL_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Marca il dispositivo come «interno» (non tracciato).
 * Apri il sito con `?ga_internal=1` oppure `?ga_internal=0` per ripristinare.
 */
export function processAnalyticsOptOutQuery() {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.search)
  if (!params.has('ga_internal')) return

  try {
    if (params.get('ga_internal') === '1') {
      window.localStorage.setItem(INTERNAL_STORAGE_KEY, '1')
    } else {
      window.localStorage.removeItem(INTERNAL_STORAGE_KEY)
    }
  } catch {
    /* storage bloccato */
  }

  params.delete('ga_internal')
  const qs = params.toString()
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  window.history.replaceState({}, '', next)
}

function injectGtagScript() {
  if (!MEASUREMENT_ID || initialized || typeof window === 'undefined') {
    return
  }
  if (isAnalyticsSkipped()) return
  if (typeof window.gtag === 'function') {
    initialized = true
    return
  }

  initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
  })
}

/** Carica GA dopo il load per non competere con LCP / thread principale. */
export function initAnalytics() {
  if (!MEASUREMENT_ID || initScheduled || typeof window === 'undefined') {
    return
  }
  if (isAnalyticsSkipped()) return
  initScheduled = true

  const run = () => injectGtagScript()

  if (document.readyState === 'complete') {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 4000 })
    } else {
      setTimeout(run, 1)
    }
  } else {
    window.addEventListener('load', () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 4000 })
      } else {
        setTimeout(run, 500)
      }
    })
  }
}

export function trackPageView(path, title) {
  if (!isAnalyticsEnabled() || typeof window.gtag !== 'function') return

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
  })
}

/**
 * Visita di /hello/[slug]. In GA4: evento `hello_page_view`, parametro `campaign_slug`.
 * Ritenta finché gtag non è pronto (lo script è differito dopo il load).
 * @returns {() => void} cleanup
 */
export function trackHelloPageView(slug) {
  const campaignSlug = String(slug ?? '')
    .trim()
    .toLowerCase()
    .slice(0, 100)
  if (!campaignSlug || typeof window === 'undefined' || !isAnalyticsEnabled()) {
    return () => {}
  }

  initAnalytics()

  let stopped = false

  const send = () => {
    if (stopped || typeof window.gtag !== 'function') return false
    window.gtag('event', 'hello_page_view', {
      campaign_slug: campaignSlug,
      page_path: window.location.pathname,
    })
    return true
  }

  if (send()) {
    return () => {
      stopped = true
    }
  }

  const timer = window.setInterval(() => {
    if (send()) window.clearInterval(timer)
  }, 250)
  const giveUp = window.setTimeout(() => {
    window.clearInterval(timer)
  }, 8000)

  return () => {
    stopped = true
    window.clearInterval(timer)
    window.clearTimeout(giveUp)
  }
}

/** Piattaforma nota o hostname semplificato per report GA4. */
export function platformFromUrl(url) {
  if (!url || typeof url !== 'string') return 'unknown'
  const lower = url.toLowerCase()
  if (lower.includes('etsy.com')) return 'etsy'
  if (lower.includes('ko-fi.com') || lower.includes('kofi.com')) return 'kofi'
  if (lower.includes('cults3d.com') || lower.includes('cults.')) return 'cults'
  if (lower.startsWith('mailto:')) return 'email'
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname
      .replace(/^www\./, '')
      .slice(0, 100)
  } catch {
    return 'unknown'
  }
}

/**
 * Click verso shop esterni (prodotto, file, /links, footer).
 * In GA4: Esplorazioni → evento `outbound_click` → dimensioni item_slug, platform, …
 */
export function trackOutboundClick({
  platform,
  linkUrl,
  itemType,
  itemSlug,
  linkId,
  linkCategory,
  linkLabel,
  isPrimary,
}) {
  if (!isAnalyticsEnabled() || typeof window.gtag !== 'function') return
  if (!linkUrl) return

  const resolvedPlatform = platform || platformFromUrl(linkUrl)

  window.gtag('event', 'outbound_click', {
    platform: resolvedPlatform,
    link_url: linkUrl,
    item_type: itemType || 'unknown',
    item_slug: itemSlug || '',
    link_id: linkId || '',
    link_category: linkCategory || '',
    link_label: linkLabel ? String(linkLabel).slice(0, 80) : '',
    is_primary: Boolean(isPrimary),
    page_path: window.location.pathname,
  })
}
